import React, { useState } from 'react';

import { blobUrl } from 'constants/Common';

import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'components/buttons/Button';
import { TextField } from 'components/inputs/TextField.react';

import { useForgotPassword } from '../hooks/useForgotPassword';

import { BackToLoginLink } from './common/BackToLoginLink';
import { ErrorMessage } from './common/ErrorMessage';
import { IllustrationBanner } from './common/IllustrationBanner';

export const ForgotPasswordView = () => {
    const intl = useIntl();
    const [email, setEmail] = useState('');
    const { emailSent, isLoading, error, submitForgotPassword } = useForgotPassword();

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (email.trim()) {
            submitForgotPassword(email.trim());
        }
    };

    if (emailSent) {
        return (
            <div>
                <IllustrationBanner src={`${blobUrl}login/pr-email-sent.png`} />
                <h2 className="tw-text-l tw-font-semibold tw-mb-8">
                    <FormattedMessage
                        id="login.forgotPassword.sent.title"
                        defaultMessage="Check your email"
                    />
                </h2>
                <p className="tw-text-neutral-subdued tw-text-s">
                    <FormattedMessage
                        id="login.forgotPassword.sent.description"
                        defaultMessage="If an account exists for {email}, we've sent a link to reset your password."
                        values={{
                            email: <strong>{email}</strong>,
                        }}
                    />
                </p>
                <BackToLoginLink />
            </div>
        );
    }

    return (
        <div>
            <h1 className="tw-text-xl tw-font-semibold tw-mb-8">
                <FormattedMessage
                    id="login.forgotPassword.title"
                    defaultMessage="Forgot password"
                />
            </h1>
            <p className="tw-text-neutral-subdued tw-text-s tw-mb-24">
                <FormattedMessage
                    id="login.forgotPassword.description"
                    defaultMessage="Enter your email address and we'll send you a link to reset your password."
                />
            </p>

            <form onSubmit={handleSubmit}>
                <TextField
                    name="email"
                    label={intl.formatMessage({
                        id: 'login.email.label',
                        defaultMessage: 'Email',
                    })}
                    placeholderText={intl.formatMessage({
                        id: 'login.email.placeholder',
                        defaultMessage: 'Enter your email',
                    })}
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                    autoFocus
                    className=""
                />
                <ErrorMessage message={error} />
                <div id="turnstile-widget-forgot" className="tw-flex tw-justify-center tw-mt-8" />
                <div className="tw-mt-16">
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isLoading}
                        wrapperClasses="tw-w-full"
                    >
                        <FormattedMessage
                            id="login.forgotPassword.submit"
                            defaultMessage="Send reset link"
                        />
                    </Button>
                </div>
            </form>
            <BackToLoginLink />
        </div>
    );
};
